import React, { useState, useEffect } from 'react';
import ReviewList from '../../components/ReviewList';
import DenseReviewList from '../../components/DenseReviewList';
import ReviewStats from '../../components/ReviewStats';
import FilterBar from '../../components/FilterBar';
import AdvancedFilterBar, { FilterOptions } from '../../components/AdvancedFilterBar';
import AddReviewModal from '../../components/AddReviewModal';
import SyncStatus from '../../components/SyncStatus';
import ManualSyncModal from '../../components/ManualSyncModal';
import NavBar from '../../components/NavBar';
import RAGSearchPanel from '../../components/RAGSearchPanel';
import DashboardSummary from '../../components/DashboardSummary';
import HighlightsOnlyView from '../../components/HighlightsOnlyView';
import PaginatedReviewList from '../../components/PaginatedReviewList';
import { Review, ReviewStats as ReviewStatsType, SyncInfo } from '../../types/Review';
import { reviewService } from '../../services/reviewService';
import { Search, Sparkles, X } from 'lucide-react';
import '../../styles/ReviewDashboardModern.css';

interface ReviewDashboardProps {
  reviews: Review[];
  loading: boolean;
  error: string | null;
  onAddReview: (review: any) => void;
  onRefresh: () => void;
}

const ReviewDashboard: React.FC<ReviewDashboardProps> = ({
  reviews,
  loading,
  error,
  onAddReview,
  onRefresh,
}) => {
  const [filteredReviews, setFilteredReviews] = useState<Review[]>(reviews || []);
  const [stats, setStats] = useState<ReviewStatsType | null>(null);
  const [syncInfo, setSyncInfo] = useState<SyncInfo | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'cards' | 'compact' | 'highlights'>('table');
  const [showStats, setShowStats] = useState(true);
  const [showAISearch, setShowAISearch] = useState(false);
  const [filters, setFilters] = useState<FilterOptions>({
    sentiment: 'all',
    rating: 'all',
    source: 'all',
    searchTerm: '',
    dateRange: 'all',
    hasHighlights: 'all',
    highlightCategory: 'all',
    highlightAngle: 'all',
    responseStatus: 'all',
    priority: 'all',
    sortBy: 'date',
    sortOrder: 'desc'
  });

  useEffect(() => {
    setFilteredReviews(reviews || []);
    loadStats();
    loadSyncInfo();
  }, [reviews]);

  useEffect(() => {
    applyFilters();
  }, [filters, reviews]);

  const loadStats = async () => {
    try {
      const statsData = await reviewService.getStats();
      setStats(statsData);
    } catch (err) {
      console.error('Failed to load stats:', err);
      // Calculate stats from local data as fallback
      if (reviews && reviews.length > 0) {
        const totalReviews = reviews.length;
        const totalRating = reviews.reduce((sum, r) => sum + (r.rating || 0), 0);
        const averageRating = totalRating / totalReviews;
        
        const sentimentCounts = reviews.reduce((acc, r) => {
          const sentiment = r.sentiment || 'neutral';
          acc[sentiment] = (acc[sentiment] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);
        
        const ratingCounts = reviews.reduce((acc, r) => {
          const rating = Math.floor(r.rating || 0);
          if (rating > 0) {
            acc[rating] = (acc[rating] || 0) + 1;
          }
          return acc;
        }, {} as Record<number, number>);
        
        setStats({
          totalReviews,
          averageRating,
          sentimentBreakdown: {
            positive: sentimentCounts.positive || 0,
            negative: sentimentCounts.negative || 0,
            neutral: sentimentCounts.neutral || 0,
            mixed: sentimentCounts.mixed || 0,
          },
          commonThemes: [],
          ratingDistribution: ratingCounts
        });
      }
    }
  };

  const loadSyncInfo = async () => {
    try {
      const info = await reviewService.getSyncInfo();
      setSyncInfo(info);
    } catch (err) {
      console.error('Failed to load sync info:', err);
      // Set mock data for now until sync manager is deployed
      setSyncInfo({
        sources: {
          'Junip (Shopify)': {
            lastSync: new Date().toISOString(),
            reviewCount: 0,
            status: 'pending',
            error: undefined
          },
          Meta: {
            lastSync: null,
            reviewCount: 0,
            status: 'pending',
            error: undefined
          }
        },
        totalReviews: reviews?.length || 0,
        lastUpdated: new Date().toISOString()
      });
    }
  };

  const handleManualSync = async (params: { source: string; startDate?: string; endDate?: string }) => {
    setSyncing(true);
    try {
      await reviewService.triggerSync(params);
      // Wait a moment for sync to start
      await new Promise(resolve => setTimeout(resolve, 2000));
      onRefresh();
      loadSyncInfo();
    } catch (err) {
      console.error('Failed to trigger sync:', err);
      alert('Failed to trigger sync. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...(reviews || [])];

    // Sentiment filter
    if (filters.sentiment !== 'all') {
      filtered = filtered.filter(r => r.sentiment === filters.sentiment);
    }

    // Rating filter
    if (filters.rating !== 'all') {
      if (filters.rating.includes('+')) {
        const minRating = parseInt(filters.rating);
        filtered = filtered.filter(r => r.rating >= minRating);
      } else if (filters.rating.includes('-')) {
        const maxRating = parseInt(filters.rating);
        filtered = filtered.filter(r => r.rating <= maxRating);
      } else {
        filtered = filtered.filter(r => r.rating === parseInt(filters.rating));
      }
    }

    // Source filter
    if (filters.source !== 'all') {
      filtered = filtered.filter(r => r.source === filters.source);
    }

    // Search filter
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(r => 
        r.review.toLowerCase().includes(term) ||
        r.title?.toLowerCase().includes(term) ||
        r.reviewer_name?.toLowerCase().includes(term) ||
        r.ai_summary?.toLowerCase().includes(term) ||
        r.key_themes?.some(t => t.toLowerCase().includes(term))
      );
    }

    // Date range filter
    if (filters.dateRange !== 'all') {
      const now = new Date();
      let startDate: Date;
      
      switch (filters.dateRange) {
        case 'today':
          startDate = new Date(now.setHours(0, 0, 0, 0));
          break;
        case 'yesterday':
          startDate = new Date(now.setDate(now.getDate() - 1));
          break;
        case 'last7days':
          startDate = new Date(now.setDate(now.getDate() - 7));
          break;
        case 'last30days':
          startDate = new Date(now.setDate(now.getDate() - 30));
          break;
        case 'last90days':
          startDate = new Date(now.setDate(now.getDate() - 90));
          break;
        case 'custom':
          if (filters.customStartDate) {
            startDate = new Date(filters.customStartDate);
            const endDate = filters.customEndDate ? new Date(filters.customEndDate) : new Date();
            filtered = filtered.filter(r => {
              const reviewDate = new Date(r.timestamp);
              return reviewDate >= startDate && reviewDate <= endDate;
            });
          }
          break;
      }
      
      if (filters.dateRange !== 'custom' && startDate!) {
        filtered = filtered.filter(r => new Date(r.timestamp) >= startDate);
      }
    }

    // Highlights filter
    if (filters.hasHighlights !== 'all') {
      filtered = filtered.filter(r => {
        const hasHighlights = r.highlights && r.highlights.length > 0;
        
        switch (filters.hasHighlights) {
          case 'yes':
            return hasHighlights;
          case 'no':
            return !hasHighlights;
          case 'quality':
            return hasHighlights && r.highlights.some(h => h.quality >= 4);
          default:
            return true;
        }
      });
    }

    // Highlight category filter (universal)
    if (filters.highlightCategory && filters.highlightCategory !== 'all') {
      filtered = filtered.filter(r => {
        if (!r.highlights || r.highlights.length === 0) {
          return false;
        }
        return r.highlights.some(highlight => 
          highlight.category === filters.highlightCategory
        );
      });
    }
    
    // Highlight angle filter (specific)
    if (filters.highlightAngle !== 'all') {
      filtered = filtered.filter(r => {
        if (!r.highlights || r.highlights.length === 0) {
          return false;
        }
        
        return r.highlights.some(highlight => {
          const angle = highlight.angle?.toLowerCase() || '';
          const filterAngle = filters.highlightAngle.toLowerCase();
          
          // Direct match for specific angles
          if (angle === filterAngle) return true;
          
          // Handle different angle naming variations (legacy support)
          switch (filterAngle) {
            case 'benefit':
              return angle.includes('benefit') || angle === 'product_benefit' || angle === 'benefit_confirmation';
            case 'emotion':
              return angle.includes('emotion') || angle === 'emotional_impact';
            case 'problem_solved':
              return angle.includes('problem') || angle === 'problemsolved';
            case 'unique_feature':
              return angle.includes('unique') || angle === 'uniquefeature';
            case 'transformation':
              return angle.includes('transformation');
            case 'comfort':
              return angle.includes('comfort');
            case 'endorsement':
              return angle.includes('endorsement');
            case 'gift_success':
              return angle.includes('gift');
            case 'repeat_purchase':
              return angle.includes('repeat');
            default:
              return angle === filterAngle;
          }
        });
      });
    }

    // Priority filter
    if (filters.priority !== 'all') {
      filtered = filtered.filter(r => r.response_priority === filters.priority);
    }

    // Sort
    filtered.sort((a, b) => {
      let compareValue = 0;
      
      switch (filters.sortBy) {
        case 'date':
          compareValue = new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
          break;
        case 'rating':
          compareValue = b.rating - a.rating;
          break;
        case 'sentiment':
          const sentimentOrder = { positive: 3, neutral: 2, mixed: 1, negative: 0 };
          compareValue = (sentimentOrder[b.sentiment as keyof typeof sentimentOrder] || 0) - 
                        (sentimentOrder[a.sentiment as keyof typeof sentimentOrder] || 0);
          break;
        case 'highlights':
          compareValue = (b.key_themes?.length || 0) - (a.key_themes?.length || 0);
          break;
      }
      
      return filters.sortOrder === 'asc' ? -compareValue : compareValue;
    });

    setFilteredReviews(filtered);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading reviews...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button onClick={onRefresh} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="review-dashboard">
      <NavBar showBackToHome={true} showUserInfo={false} />
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <h1>Review Highlights Dashboard</h1>
            <div className="header-meta">
              <p className="dashboard-subtitle">
                Analyzing {stats?.totalReviews || reviews?.length || 0} customer reviews
              </p>
              {syncInfo && syncInfo.lastUpdated && (
                <span className="dashboard-date-info">
                  Last updated: {new Date(syncInfo.lastUpdated).toLocaleDateString('en-US', { 
                    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                  })}
                </span>
              )}
            </div>
          </div>
          <div className="header-actions">
            <button onClick={() => setShowSyncModal(true)} className="sync-button" disabled={syncing}>
              {syncing ? 'Syncing...' : 'Manual Sync'}
            </button>
            <button onClick={() => setShowAddModal(true)} className="add-button">
              Add Review
            </button>
            <button onClick={onRefresh} className="refresh-button">
              Refresh
            </button>
          </div>
        </div>
      </header>

      <div className="content-area">
        {/* Enhanced Dashboard Summary */}
        {showStats && (
          <DashboardSummary reviews={filteredReviews} />
        )}
        
        {syncInfo && <SyncStatus syncInfo={syncInfo} onRefresh={loadSyncInfo} />}
        
        {/* AI Search Panel - Always visible */}
        <div style={{ marginBottom: '20px' }}>
          <RAGSearchPanel />
        </div>

        {/* Controls Section */}
        <div className="controls-section">
          <div className="controls-content">
            {/* View Controls */}
            <div className="view-controls">
              <div className="view-toggles">
                <button 
                  className={`toggle-btn ${showStats ? 'active' : ''}`}
                  onClick={() => setShowStats(!showStats)}
                >
                  {showStats ? 'Hide Stats' : 'Show Stats'}
                </button>
              </div>
              <div className="view-mode-selector">
                <button 
                  className={`view-mode-btn ${viewMode === 'table' ? 'active' : ''}`}
                  onClick={() => setViewMode('table')}
                  title="Table View"
                >
                  ⚏
                </button>
                <button 
                  className={`view-mode-btn ${viewMode === 'compact' ? 'active' : ''}`}
                  onClick={() => setViewMode('compact')}
                  title="Compact View"
                >
                  ☰
                </button>
                <button 
                  className={`view-mode-btn ${viewMode === 'cards' ? 'active' : ''}`}
                  onClick={() => setViewMode('cards')}
                  title="Card View"
                >
                  ⊞
                </button>
                <button 
                  className={`view-mode-btn ${viewMode === 'highlights' ? 'active' : ''}`}
                  onClick={() => setViewMode('highlights')}
                  title="Highlights Only"
                >
                  💬
                </button>
              </div>
            </div>
          </div>
        </div>

        <AdvancedFilterBar
          filters={filters}
          onFilterChange={setFilters}
          sources={[...new Set((reviews || []).map(r => r.source))]}
          products={[...new Set((reviews || []).map(r => r.title || 'Unknown').filter(Boolean))]}
          totalCount={stats?.totalReviews || reviews?.length || 0}
          filteredCount={filteredReviews.length}
          onExport={() => {
            // TODO: Implement CSV export
            console.log('Exporting reviews...');
          }}
        />

        {viewMode === 'highlights' ? (
          <HighlightsOnlyView 
            reviews={filteredReviews.filter(r => r.highlights && r.highlights.length > 0)}
          />
        ) : (
          // Use pagination for better performance with large datasets
          <PaginatedReviewList
            reviews={filteredReviews}
            viewMode={viewMode}
            onUpdate={(id: string, updates: any) => {
              reviewService.updateReview(id, updates);
              onRefresh();
            }}
            onDelete={(id: string) => {
              reviewService.deleteReview(id);
              onRefresh();
            }}
            onBulkAction={(action: string, ids: string[]) => {
              console.log('Bulk action:', action, ids);
              // TODO: Implement bulk actions
            }}
          />
        )}
      </div>

      {showAddModal && (
        <AddReviewModal
          onClose={() => setShowAddModal(false)}
          onSubmit={(review: any) => {
            onAddReview(review);
            setShowAddModal(false);
          }}
        />
      )}

      {showSyncModal && (
        <ManualSyncModal
          onClose={() => setShowSyncModal(false)}
          onSync={handleManualSync}
          sources={['Junip (Shopify)', 'Meta']}
        />
      )}
    </div>
  );
};

export default ReviewDashboard;